import { type PropsWithChildren, type ReactNode } from "react"
import {
  Columns,
  SortAscending,
  SortDescending,
  Storefront,
  TreeView,
  Warehouse,
} from "@phosphor-icons/react"
import { startCase } from "lodash"

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import { FilterPill } from "~/components/planning/FilterPill"
import { Tooltip } from "~/components/reusable/Tooltip"
import { ViewScopeIcon } from "~/components/planning/TableViewSelector"
import { UserAvatar } from "~/components/reusable/users/UserAvatar"
import { useChannels } from "~/hooks/useChannels"
import { useExtraDataDisplayNameMap } from "~/hooks/useSku"
import { getColumnNaming } from "~/components/planning/ProductTable/utils/columnUtils"
import { ExtraDataTable, getExtraDataDisplayName } from "~/utils/dynamicTableUtils"
import { isAgGridInternalColumn } from "~/hooks/useGridState"
import { useFilterFields } from "~/hooks/useFilterFields"
import { useUserLookup } from "~/hooks/useUsers"
import { api } from "~/utils/api"
import {
  getFilterIcon,
  splitFiltersIntoGroupUnits,
  type Filter,
  type FilterFieldType,
} from "~/utils/filter"
import { formatFilterLabel } from "~/utils/filterLabel"
import { cn } from "~/utils/helpers"
import { DateFormatType, formatDate, formatTimeAgo } from "~/utils/datetime"
import { type ViewActor, type SavedTableView } from "~/server/api/routers/tableView"
import { getViewActivity, type ViewDetailsSubject } from "~/utils/viewDetailsVisibility"
import { type PresetView } from "~/components/planning/presetView"
import { useCompany } from "~/hooks/useCompany"
import { resolveDefaultGrouping } from "~/utils/productTableUtils"
import { HAKIO_ONLY_TOOLTIP } from "~/constants/hakioOnly"

type Props = PropsWithChildren &
  ViewDetailsSubject & {
    /**
     * Page-specific filter field *definitions* appended to the global `filterFields` catalog
     * (see `useFilterFields`). Needed so a saved filter referencing a page-only field resolves
     * to a proper label/type/icon instead of "Unknown filter". The one field this handles today
     * is the Fashion buy/supply plan's "SKU Size" (`skuSizeFilterField`); undefined elsewhere.
     */
    extraFilterFields?: FilterFieldType[]
    /** Controlled open state — the parent suppresses the card while the options menu is open. */
    open?: boolean
    onOpenChange?: (open: boolean) => void
  }

/**
 * Hover card summarising a saved view: its grouping, applied filters (read-only pills, OR
 * groups included) and channel/warehouse scope. Compact and icon-led — reuses the shared
 * `HoverCard`, the app's `FilterPill`, and the same label formatter as the filter popover.
 * A built-in preset gets the same card from its `details`: description, the rules in code that
 * pick its rows and its default grouping.
 */
export function ViewDetailsHoverCard({
  view,
  preset,
  extraFilterFields,
  open,
  onOpenChange,
  children,
}: Props) {
  return (
    <HoverCard open={open} onOpenChange={onOpenChange} openDelay={300}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent className="w-max min-w-72 max-w-[34rem] p-0" align="start" side="bottom">
        {view ? (
          <ViewDetailsHoverCardBody view={view} extraFilterFields={extraFilterFields} />
        ) : (
          <PresetDetailsBody preset={preset} extraFilterFields={extraFilterFields} />
        )}
      </HoverCardContent>
    </HoverCard>
  )
}

/** Display label for a column / grouping id, as the column chooser names it. */
function useColumnLabel(extraFilterFields: FilterFieldType[] | undefined) {
  // Resolve against the global field catalog PLUS any page-specific field definitions passed in
  // via extraFilterFields (e.g. the Fashion buy/supply plan's "SKU Size"). Without those, a saved
  // filter on such a field would fall through to "Unknown filter".
  const { allFilterFields } = useFilterFields("", extraFilterFields)
  const { data: extraDataNames } = useExtraDataDisplayNameMap()
  const columnLabel = (colId: string) => {
    if (colId.endsWith("(collection-extradata)")) {
      return getExtraDataDisplayName(
        colId.replace("(collection-extradata)", ""),
        ExtraDataTable.COLLECTION_ON_SKU,
        extraDataNames,
      )
    }
    if (colId.endsWith("(extradata)")) {
      return getExtraDataDisplayName(
        colId.replace("(extradata)", ""),
        ExtraDataTable.SKU,
        extraDataNames,
      )
    }
    return (
      getColumnNaming(colId)?.columnChooserTitle ??
      allFilterFields.find((f) => f.value === colId)?.name ??
      startCase(colId)
    )
  }
  return { allFilterFields, columnLabel }
}

function ViewDetailsHoverCardBody({
  view,
  extraFilterFields,
}: {
  view: SavedTableView
  extraFilterFields?: FilterFieldType[]
}) {
  const { allFilterFields, columnLabel } = useColumnLabel(extraFilterFields)
  const { getUserName } = useUserLookup()
  const { data: channels } = useChannels()
  const { data: warehouses } = api.warehouse.get.useQuery()

  const groupByLabels = view.aggregationMethods.map(columnLabel)
  const layout = summarizeLayout(view)
  const sortLabel = layout.sort && columnLabel(layout.sort.colId)
  const filterUnits = splitFiltersIntoGroupUnits(view.filters)
  const channelName = view.channel
    ? (channels?.find((c) => c.id === view.channel)?.name ?? view.channel)
    : null
  const warehouseName = view.warehouseId
    ? (warehouses?.find((w) => w.id === view.warehouseId)?.name ?? view.warehouseId)
    : null

  const hasTable =
    groupByLabels.length > 0 ||
    layout.columnCount !== null ||
    !!sortLabel ||
    channelName ||
    warehouseName

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center gap-1.5 min-w-0">
        <ViewScopeIcon type={view.type} />
        <span className="label-base text-fg-base truncate min-w-0">{view.name}</span>
      </div>

      {view.description && (
        <p className="paragraph-base text-fg-subtle whitespace-pre-wrap">{view.description}</p>
      )}

      {view.filters.length > 0 && (
        <Section label="Filters">
          {filterUnits.map((unit, unitIndex) =>
            unit.filters.length > 1 ? (
              <div
                key={unitIndex}
                className="flex flex-wrap items-center gap-1.5 rounded-md border border-dashed border-strong p-1 max-w-full min-w-0"
              >
                {unit.filters.map((filter, filterIndex) => (
                  <FilterSummaryPill
                    key={filterIndex}
                    filter={filter}
                    allFilterFields={allFilterFields}
                    getUserName={getUserName}
                  />
                ))}
              </div>
            ) : (
              <FilterSummaryPill
                key={unitIndex}
                filter={unit.filters[0]!}
                allFilterFields={allFilterFields}
                getUserName={getUserName}
              />
            ),
          )}
        </Section>
      )}

      {hasTable && (
        <Section label="Table">
          {groupByLabels.length > 0 && (
            <MetaChip icon={<TreeView />}>Group by: {groupByLabels.join(" › ")}</MetaChip>
          )}
          {layout.columnCount !== null && (
            <MetaChip icon={<Columns />}>
              {layout.columnCount} {layout.columnCount === 1 ? "column" : "columns"}
            </MetaChip>
          )}
          {layout.sort && sortLabel && (
            <MetaChip icon={layout.sort.desc ? <SortDescending /> : <SortAscending />}>
              {sortLabel}
            </MetaChip>
          )}
          {channelName && <MetaChip icon={<Storefront />}>{channelName}</MetaChip>}
          {warehouseName && <MetaChip icon={<Warehouse />}>{warehouseName}</MetaChip>}
        </Section>
      )}

      <ViewAuditFooter view={view} />
    </div>
  )
}

function PresetDetailsBody({
  preset,
  extraFilterFields,
}: {
  preset: PresetView
  extraFilterFields?: FilterFieldType[]
}) {
  const { columnLabel } = useColumnLabel(extraFilterFields)
  const { data: company } = useCompany()
  const { description, rules = [], grouping } = preset.details ?? {}
  const resolved =
    grouping &&
    resolveDefaultGrouping(
      grouping.initialAggregation,
      grouping.followCompanyDefault ? company?.defaultAggregationLevel : null,
    )

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center gap-1.5 min-w-0">
        {preset.icon && <span className="flex items-center shrink-0">{preset.icon}</span>}
        <span className="label-base text-fg-base truncate min-w-0">{preset.name}</span>
      </div>

      {description && <p className="paragraph-base text-fg-subtle max-w-[50ch]">{description}</p>}

      {rules.length > 0 && (
        <Section label="Criteria">
          {rules.map((rule) => (
            <FilterPill
              key={rule.label}
              asChild
              size="xs"
              icon={rule.icon}
              className={READ_ONLY_PILL}
            >
              <span className="min-w-0 break-words">{rule.label}</span>
            </FilterPill>
          ))}
        </Section>
      )}

      {resolved && (
        <Section label="Table">
          <MetaChip icon={<TreeView />}>
            Opens grouped by: {resolved.aggregations.map(columnLabel).join(" › ")}
            {resolved.fromCompany ? " (company setting)" : ""}
          </MetaChip>
        </Section>
      )}

      {preset.hakioOnly && (
        <div className="border-t border-base pt-2 label-sm text-fg-subtle">
          {HAKIO_ONLY_TOOLTIP}
        </div>
      )}
    </div>
  )
}

/**
 * Attribution footer: a summary line for the latest event ("{name} · Updated/Created {relative}").
 * If the view was edited and creation details survived redaction, it becomes a hover trigger
 * revealing the full created/updated breakdown.
 */
function ViewAuditFooter({ view }: { view: SavedTableView }) {
  const { getUserName, getUserImage } = useUserLookup()
  const activity = getViewActivity(view.audit)
  if (!activity || !activity.latestAt) return null

  const name = activity.latestActor ? getUserName(activity.latestActor.id) : ""
  const verb = activity.wasEdited ? "Updated" : "Created"
  const relative = formatTimeAgo(activity.latestAt)

  const summary = (
    <div className="flex items-center gap-1.5 min-w-0 label-sm text-fg-subtle">
      {activity.latestActor && (
        <UserAvatar
          size={16}
          name={name}
          email=""
          picture={getUserImage(activity.latestActor.id) || undefined}
        />
      )}
      <span className="truncate min-w-0">
        {name ? `${name} · ` : ""}
        {verb} {relative}
      </span>
    </div>
  )

  const showDetails = activity.wasEdited && !!activity.createdAt

  return (
    <div className="border-t border-base pt-2">
      {showDetails ? (
        // A nested Radix HoverCard can't be reliably triggered inside another (pointer transition
        // closes the outer one), so the breakdown uses a Tooltip instead.
        <Tooltip
          side="bottom"
          align="start"
          delayDuration={150}
          className="max-w-none" // fit content; override the 300px cap so dates don't clip
          body={
            <div className="flex flex-col gap-1.5">
              <AuditDetailLine
                verb="Created"
                actor={activity.createdActor}
                at={activity.createdAt!}
              />
              <AuditDetailLine
                verb="Updated"
                actor={activity.editedActor}
                at={activity.editedAt!}
              />
            </div>
          }
        >
          <div className="w-fit cursor-help">{summary}</div>
        </Tooltip>
      ) : (
        summary
      )}
    </div>
  )
}

/** One "Created/Updated by {name} · {absolute date}" row inside the full-detail hover card. */
function AuditDetailLine({
  verb,
  actor,
  at,
}: {
  verb: string
  actor: ViewActor | null
  at: string
}) {
  const { getUserName, getUserImage } = useUserLookup()
  const name = actor ? getUserName(actor.id) : ""
  const absolute = formatDate(at, DateFormatType.WithTime)
  return (
    <div className="flex items-center gap-1.5 label-sm text-fg-subtle">
      {actor && (
        <UserAvatar size={16} name={name} email="" picture={getUserImage(actor.id) || undefined} />
      )}
      <span className="whitespace-nowrap">
        {verb}
        {name ? ` by ${name}` : ""} · {absolute}
      </span>
    </div>
  )
}

/** Read-only filter pill using the app's standard fully-rounded `FilterPill`. */
export function FilterSummaryPill({
  filter,
  allFilterFields,
  getUserName,
}: {
  filter: Filter
  allFilterFields: FilterFieldType[]
  getUserName: (id: string) => string
}) {
  return (
    <FilterPill
      asChild
      size="xs"
      icon={getFilterIcon(filter.field, allFilterFields)}
      className={READ_ONLY_PILL}
    >
      <span className="min-w-0 break-words">
        {formatFilterLabel(filter, allFilterFields, getUserName)}
      </span>
    </FilterPill>
  )
}

/**
 * Compact icon-led chip for a single meta value (group by, columns, sort, channel, warehouse).
 * Uses the outline `FilterPill` variant so it reads as context rather than an applied filter.
 */
function MetaChip({ icon, children }: PropsWithChildren<{ icon: ReactNode }>) {
  return (
    <FilterPill
      asChild
      size="xs"
      variant="neutralOutline"
      icon={icon}
      // Solid, not the variant's dashed outline: dashed marks an OR group of filters on this card.
      className={cn(READ_ONLY_PILL, "outline-solid")}
    >
      <span className="min-w-0 break-words">{children}</span>
    </FilterPill>
  )
}

// Read-only: no pointer events, so FilterPill's hover styles never fire and nothing in the card
// looks clickable. Long values wrap inside the card's max width instead of being cut off.
const READ_ONLY_PILL =
  "pointer-events-none h-auto min-h-6 max-w-full min-w-0 justify-start whitespace-normal rounded-xl text-left [&>svg]:shrink-0"

function Section({ label, children }: PropsWithChildren<{ label: string }>) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="label-sm text-fg-subtle">{label}</span>
      <div className="flex flex-wrap items-center gap-1.5 min-w-0">{children}</div>
    </div>
  )
}

/** Visible column count and primary sort from the saved grid state, when it has them. */
function summarizeLayout(view: SavedTableView) {
  const isUserColumn = (colId: string) => !isAgGridInternalColumn(colId)
  const ordered = view.gridState?.columnOrder?.orderedColIds?.filter(isUserColumn)
  const hidden = new Set(view.gridState?.columnVisibility?.hiddenColIds ?? [])
  const sort = view.gridState?.sort?.sortModel?.find((s) => isUserColumn(s.colId))
  return {
    columnCount: ordered ? ordered.filter((colId) => !hidden.has(colId)).length : null,
    sort: sort ? { colId: sort.colId, desc: sort.sort === "desc" } : null,
  }
}
